import React, { Dispatch, SetStateAction, useState } from 'react'
import { Table, Button, Checkbox, Divider, Dropdown, Empty, Input, Menu, message } from 'antd-v4'
import { Icon } from 'antd'
import { observer } from 'mobx-react-lite'
import { CurrentCompanyStore } from '../stores/CurrentCompanyStore'
import { inject } from 'mobx-react'
import { ILocation } from '@getgreenline/homi-shared'
import { BlazeButton } from './CustomButtons/BlazeButton'
import { InsertRowRightOutlined } from '@ant-design/icons'
import { CustomDatePicker } from './CustomDatePicker'
import moment from 'moment'
import { RangePickerValue } from 'antd/lib/date-picker/interface'
import FilterSVG from '../styles/icons/Filter.svg'

interface Props<T> {
  currentCompanyStore?: CurrentCompanyStore
  dataSource: Array<T>
  columns: Array<Column>
  selectedColumns: Array<Column>
  isLoading: boolean
  rowSelection: any
  columnDropdownOverlay: any
  applyFilterButtonHandler: (
    locationIds: number[],
    searchFields: SearchField[],
    rangePicker?: RangePickerValue,
  ) => Promise<void>
  verticalScroll: number | string
  resetFilter: () => Promise<void>
  allAvailableFilterOptions: Array<string>
  filterOptions: Array<FilterOption>
  searchFields: SearchField[]
  setSearchFields: Dispatch<SetStateAction<SearchField[]>>
  rangePickerValue?: RangePickerValue
  onRangePickerChange?: (rangePickerValue: RangePickerValue) => void
  rangePickerLimits?: RangePickerField
}

export interface Column {
  title: string
  dataIndex: string
  key: string
  hide: boolean
}
interface GenericProps {
  key: string
}

export interface SearchField {
  field: string
  value: string | string[]
}

export interface RangePickerField {
  minStartDate?: moment.Moment
  maxEndDate?: moment.Moment
}

export interface FilterOption {
  name: string
  enum: OptionEnum[]
  optional: boolean
  dropdownVisible?: boolean
}

interface OptionEnum {
  key: number
  text: string
  value: string
}

const _CustomTable = <T extends GenericProps>(props: Props<T>) => {
  const {
    dataSource,
    isLoading,
    selectedColumns,
    rowSelection,
    verticalScroll,
    applyFilterButtonHandler,
    searchFields,
    setSearchFields,
    rangePickerValue,
    onRangePickerChange,
    rangePickerLimits: rangePickerField,
  } = props
  const locations: ILocation[] = props.currentCompanyStore?.locations || []
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>(
    locations.map((location) => location.id),
  )
  const [locationDropdownVisible, setLocationDropdownVisible] = useState<boolean>(false)
  const [filterDropdownVisible, setFilterDropdownVisible] = useState<boolean>(false)
  const [columnDropdownVisible, setColumnDropdownVisible] = useState<boolean>(false)
  const [isAllLocationSelected, setIsAllLocationSelected] = useState<boolean>(true)
  const [selectedAddOnFilterNames, setSelectedAddOnFilterNames] = useState<string[]>([])
  const [filterOptionState, setFilterOptionState] = useState<FilterOption[]>(props.filterOptions)

  const onSearchByEnterKey = () => {
    const doesSearchFieldExist = !!searchFields.find((searchField) => searchField.value)
    if (!doesSearchFieldExist) {
      return message.warn(`Please enter a value to filter by`)
    }
    props.applyFilterButtonHandler(selectedLocationIds, searchFields, rangePickerValue)
  }

  const LocationDropdownOverlay = () => (
    <Menu>
      <Menu.Item key='all-none' className='w-100'>
        <Button
          type='link'
          onClick={() => handleLocationSelectAll(true)}
          className='mr-2'
          disabled={isAllLocationSelected}
        >
          All
        </Button>
        <Button
          type='link'
          onClick={() => handleLocationSelectAll(false)}
          disabled={selectedLocationIds.length === 0}
        >
          None
        </Button>
      </Menu.Item>
      <Divider className='px-2 my-2' />
      {locations.map((location) => (
        <Menu.Item key={location.id}>
          <Checkbox
            checked={selectedLocationIds.includes(location.id)}
            onClick={() => setLocationCheckboxState(location.id)}
          >
            {location.name}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  )

  const handleLocationSelectAll = (all: boolean) => {
    if (all) {
      const locationIds = locations.map((location) => location.id)
      setIsAllLocationSelected(true)
      setSelectedLocationIds(locationIds)
    } else {
      setIsAllLocationSelected(false)
      setSelectedLocationIds([])
    }
  }

  const setLocationCheckboxState = (selectedLocationId: number) => {
    const alreadyChecked = selectedLocationIds.includes(selectedLocationId)
    const newLocationIds = alreadyChecked
      ? selectedLocationIds.filter((locationId) => locationId !== selectedLocationId)
      : [...selectedLocationIds, selectedLocationId]
    setSelectedLocationIds(newLocationIds)
    if (isAllLocationSelected) {
      setIsAllLocationSelected(false)
    }
  }

  const FilterDropdownOverlay = () => (
    <Menu className='gr-filter-menu'>
      <Menu.Item>
        <Checkbox
          checked={allColumnFiltersApplied()}
          onClick={() => {
            handleSelectAll()
          }}
          style={{ fontWeight: 'bold' }}
        >
          Select All
        </Checkbox>
      </Menu.Item>

      <Divider className='px-2 my-2' />
      {props.allAvailableFilterOptions.map((availableFilterOption) => {
        const option = props.filterOptions.find(
          (filterOption) => filterOption.name === availableFilterOption,
        )
        if (option?.optional) {
          return (
            <Menu.Item key={availableFilterOption}>
              <Checkbox
                checked={selectedAddOnFilterNames.includes(availableFilterOption)}
                onClick={() => {
                  setFilterCheckboxState(availableFilterOption)
                }}
              >
                {availableFilterOption}
              </Checkbox>
            </Menu.Item>
          )
        }
      })}
    </Menu>
  )

  const handleSelectAll = () => {
    if (allColumnFiltersApplied()) {
      setSelectedAddOnFilterNames([])
      const newSearchFields = searchFields.filter(
        (searchField) => !selectedAddOnFilterNames.includes(searchField.field),
      )
      setSearchFields(newSearchFields)
    } else {
      const newFilters: string[] = []
      const newSearchFilter: SearchField[] = []
      props.filterOptions
        .filter((filterOption) => filterOption.optional)
        .forEach((option) => {
          const filterApplied = selectedAddOnFilterNames.includes(option.name)
          if (!filterApplied) {
            newFilters.push(option.name)
            newSearchFilter.push({
              field: option.name,
              value: '',
            })
          }
        })
      setSelectedAddOnFilterNames([...selectedAddOnFilterNames, ...newFilters])
      setSearchFields([...searchFields, ...newSearchFilter])
    }
  }

  const allColumnFiltersApplied = () => {
    const result = props.filterOptions
      .filter((op) => op.optional)
      .every((option) => {
        return selectedAddOnFilterNames.includes(option.name)
      })

    return result
  }

  const setFilterCheckboxState = (selectedFilterName: string) => {
    const filterAlreadySelected = selectedAddOnFilterNames.includes(selectedFilterName)

    if (filterAlreadySelected) {
      setUncheckedFilter(selectedFilterName)
    } else {
      setCheckedFilter(selectedFilterName)
    }
  }

  const SearchFieldOptionDropdownOverlay = (filterOption: FilterOption) => {
    const newSearchFields = [...searchFields]
    const currentSearchField = newSearchFields.find(
      (searchField) => searchField.field === filterOption.name,
    )
    if (!currentSearchField) return <></>
    return (
      <Menu>
        <Menu.Item key='all-none' className='text-right w-100'>
          <Button
            type='link'
            className='mr-2'
            onClick={() => handleSearchFieldOptionSelectAll(currentSearchField, filterOption, true)}
            disabled={currentSearchField.value.length === filterOption.enum.length}
          >
            All
          </Button>
          <Button
            type='link'
            onClick={() =>
              handleSearchFieldOptionSelectAll(currentSearchField, filterOption, false)
            }
            disabled={currentSearchField.value.length === 0}
          >
            None
          </Button>
        </Menu.Item>
        <Divider className='px-2 my-2' />

        {filterOption.enum.map((optionEnum: OptionEnum) => (
          <Menu.Item key={optionEnum.key}>
            <Checkbox
              checked={currentSearchField.value.includes(optionEnum.value)}
              onClick={() => {
                setSearchFieldOptionCheckboxState(currentSearchField, optionEnum.value)
              }}
            >
              {optionEnum.text}
            </Checkbox>
          </Menu.Item>
        ))}
      </Menu>
    )
  }

  const setSearchFieldOptionCheckboxState = (
    currentSearchField: SearchField,
    optionEnumValue: string,
  ) => {
    const alreadyChecked = currentSearchField.value.includes(optionEnumValue)
    if (Array.isArray(currentSearchField.value)) {
      const newOptionEnums = alreadyChecked
        ? currentSearchField.value.filter(
            (searchFieldValue) => searchFieldValue !== optionEnumValue,
          )
        : [...currentSearchField.value, optionEnumValue]
      currentSearchField.value = newOptionEnums
      setSearchFields([...searchFields, currentSearchField])
    }
  }

  const handleSearchFieldOptionSelectAll = (
    currentSearchField: SearchField,
    filterOption: FilterOption,
    all: boolean,
  ) => {
    if (all) {
      const allOptionEnums = filterOption.enum.map((enumOption) => enumOption.value)
      currentSearchField.value = allOptionEnums
    } else {
      currentSearchField.value = []
    }
    return setSearchFields([...searchFields, currentSearchField])
  }
  const setCheckedFilter = (selectedFilterName: string) => {
    setSelectedAddOnFilterNames([...selectedAddOnFilterNames, selectedFilterName])

    setSearchFields([
      ...searchFields,
      {
        field: selectedFilterName,
        value: '',
      },
    ])
  }

  const setUncheckedFilter = (selectedFilterName: string) => {
    const newFilters = selectedAddOnFilterNames.filter((filter) => filter !== selectedFilterName)
    setSelectedAddOnFilterNames(newFilters)
    const newSearchFields = searchFields.filter(
      (searchField) => searchField.field !== selectedFilterName,
    )
    setSearchFields(newSearchFields)
  }

  const resetFilters = async () => {
    setSelectedAddOnFilterNames([])
    const allLocationIds = locations.map((location) => location.id)
    setSelectedLocationIds(allLocationIds)
    props.resetFilter()
  }

  return (
    <div>
      <div
        className='d-flex justify-content-between'
        style={{
          marginBottom: 16,
          padding: 8,
          paddingBottom: 0,
          backgroundColor: 'white',
        }}
      >
        <div className='d-flex flex-wrap'>
          <Dropdown
            className='gr-filter-column-item gr-filter-gutter'
            trigger={['click']}
            overlay={FilterDropdownOverlay}
            onVisibleChange={() => setFilterDropdownVisible(!filterDropdownVisible)}
            visible={filterDropdownVisible}
          >
            <Button
              type={filterDropdownVisible ? 'default' : 'link'}
              className='pr-5 gr-filter-button'
            >
              <img src={FilterSVG} style={{ width: 25 }} alt='' />
            </Button>
          </Dropdown>
          <Dropdown
            className='mr-2 mb-2'
            trigger={['click']}
            overlay={LocationDropdownOverlay}
            placement='bottomCenter'
            onVisibleChange={() => setLocationDropdownVisible(!locationDropdownVisible)}
            visible={locationDropdownVisible}
          >
            <Button>
              {isAllLocationSelected ? (
                <span>Locations (all)</span>
              ) : (
                <span>Locations ({selectedLocationIds.length})</span>
              )}
              <Icon type='down' />
            </Button>
          </Dropdown>

          {rangePickerField && (
            <div className='mr-2 mb-2'>
              <CustomDatePicker
                value={rangePickerValue}
                minStartDate={rangePickerField.minStartDate}
                maxEndDate={rangePickerField.maxEndDate}
                onChange={(value) => {
                  if (onRangePickerChange) {
                    onRangePickerChange(value)
                  }
                }}
              />
            </div>
          )}

          {props.filterOptions.map((filterOption: FilterOption) => {
            if (!selectedAddOnFilterNames.includes(filterOption.name) && filterOption.optional) {
              return
            }
            const newSearchFields = [...searchFields]
            const currentOption = newSearchFields.find(
              (searchField) => searchField.field === filterOption.name,
            )
            if (!filterOption.enum.length) {
              if (Array.isArray(currentOption?.value)) return
              return (
                <Input
                  key={filterOption.name}
                  className='gr-filter-item gr-filter-gutter mb-2'
                  placeholder={filterOption.name}
                  value={
                    searchFields.find((searchField) => searchField.field === filterOption.name)
                      ?.value
                  }
                  onChange={(e) => {
                    const newSearchFields = [...searchFields]
                    const currentOption = newSearchFields.find(
                      (searchField) => searchField.field === filterOption.name,
                    )
                    if (currentOption) {
                      currentOption.value = e.target.value
                    }
                    setSearchFields(newSearchFields)
                  }}
                  onPressEnter={onSearchByEnterKey}
                />
              )
            }

            const currentStateOption = filterOptionState.find(
              (option) => option.name === filterOption.name,
            )
            return (
              <>
                <Dropdown
                  className='mr-2 mb-2'
                  trigger={['click']}
                  overlay={SearchFieldOptionDropdownOverlay(filterOption)}
                  placement='bottomCenter'
                  onVisibleChange={() => {
                    if (currentStateOption) {
                      currentStateOption.dropdownVisible = !currentStateOption?.dropdownVisible
                      setFilterOptionState([...filterOptionState, currentStateOption])
                    }
                  }}
                  visible={currentStateOption?.dropdownVisible}
                >
                  <Button>
                    {currentOption?.value.length === filterOption.enum.length ? (
                      <span>{filterOption.name}s (all)</span>
                    ) : (
                      <span>
                        {currentOption?.field} ({currentOption?.value.length})
                      </span>
                    )}
                    <Icon type='down' />
                  </Button>
                </Dropdown>
              </>
            )
          })}

          {selectedLocationIds.length > 0 ? (
            <BlazeButton
              buttonProps={{
                onClick: () => {
                  applyFilterButtonHandler(
                    isAllLocationSelected ? [] : selectedLocationIds,
                    searchFields,
                    rangePickerValue,
                  )
                },
                type: 'primary',
                className: 'mr-2 mb-2',
              }}
              titleProps={{
                title: 'Apply',
              }}
            />
          ) : (
            <Button disabled>Apply</Button>
          )}

          <BlazeButton
            buttonProps={{
              className: 'mb-2',
              type: 'link',
              onClick: resetFilters,
            }}
            titleProps={{ title: 'Reset' }}
          />
        </div>
      </div>

      <div className='text-right'>
        <Dropdown
          trigger={['click']}
          overlay={props.columnDropdownOverlay}
          placement='bottomCenter'
          onVisibleChange={() => setColumnDropdownVisible(!columnDropdownVisible)}
          visible={columnDropdownVisible}
        >
          <Button>
            <InsertRowRightOutlined />
            <Icon type='down' />
          </Button>
        </Dropdown>

        {selectedColumns.length > 0 ? (
          <Table<T>
            dataSource={dataSource}
            rowKey={(data) => data.key}
            className='no-background-table show-horizontal-scrollbar'
            rowClassName={() => 'background-white'}
            columns={selectedColumns}
            pagination={false}
            scroll={{ x: 'max-content', y: verticalScroll ? verticalScroll : 'max-content' }}
            loading={isLoading}
            rowSelection={selectedColumns.length > 0 ? rowSelection : null}
          />
        ) : (
          <Table<T>
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'No columns selected'} />
              ),
            }}
          />
        )}
      </div>
    </div>
  )
}

export const CustomTable = inject('currentCompanyStore')(observer(_CustomTable))
